import React, { useState } from 'react';
import {
  Badge,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from '@mui/material';
import {
  useGetVoiceEnrollmentStatusQuery,
  useResetVoiceEnrollmentMutation,
} from '../store/api';
import { formatDateTime } from '../utils/formatting';

interface VoiceEnrollmentSectionProps {
  userId: string;
}

export function VoiceEnrollmentSection({ userId }: VoiceEnrollmentSectionProps) {
  const {
    data: enrollmentStatus,
    isLoading,
    error,
    refetch,
  } = useGetVoiceEnrollmentStatusQuery({ userId }, { skip: !userId });

  const [resetEnrollment, { isLoading: resetting }] = useResetVoiceEnrollmentMutation();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [resetResult, setResetResult] = useState<{
    enrolled_deleted: number;
    session_deleted: number;
    mongo_updated: boolean;
  } | null>(null);

  const handleResetClick = () => {
    setConfirmDialogOpen(true);
  };

  const handleConfirmReset = async () => {
    setConfirmDialogOpen(false);
    setResetResult(null);
    try {
      const result = await resetEnrollment({ userId }).unwrap();
      setResetResult(result);
      // Refetch enrollment status after reset
      void refetch();
    } catch (error) {
      console.error('Failed to reset voice enrollment:', error);
    }
  };

  const handleCancelReset = () => {
    setConfirmDialogOpen(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-4">
        <CircularProgress size={24} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-red-600 text-sm">
        Error loading voice enrollment status. Please try again.
      </div>
    );
  }

  const isEnrolled = enrollmentStatus?.is_enrolled ?? false;

  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-6 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700">Enrolled:</span>
          {isEnrolled ? (
            <Badge color="success">Yes</Badge>
          ) : (
            <Badge color="error">No</Badge>
          )}
        </div>
        {isEnrolled && enrollmentStatus?.enrolled_at && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700">Enrolled At:</span>
            <span className="text-sm text-gray-900">
              {formatDateTime(enrollmentStatus.enrolled_at)}
            </span>
          </div>
        )}
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-700">Data Points:</span>
          <span className="text-sm text-gray-900 font-medium">
            {enrollmentStatus?.data_point_count ?? 0}
          </span>
        </div>
        {resetResult && (
          <div className="px-3 py-1 bg-green-50 border border-green-200 rounded">
            <Typography variant="body2" className="text-green-700 text-xs">
              Reset: {resetResult.enrolled_deleted} enrolled, {resetResult.session_deleted} session
            </Typography>
          </div>
        )}
      </div>
      <Button
        variant="outlined"
        color="error"
        size="small"
        disabled={resetting || !isEnrolled}
        onClick={handleResetClick}
      >
        Reset Enrollment
      </Button>

      <Dialog open={confirmDialogOpen} onClose={handleCancelReset}>
        <DialogTitle>Confirm Reset Voice Enrollment</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to reset the voice enrollment for this user? This will delete all
            voice embedding data including:
          </Typography>
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>All enrolled voice embeddings</li>
            <li>All session voice embeddings</li>
            <li>Enrollment status from the database</li>
          </ul>
          <Typography className="mt-3 font-medium text-red-600">
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelReset} disabled={resetting}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmReset}
            color="error"
            variant="contained"
            disabled={resetting}
          >
            {resetting ? <CircularProgress size={20} /> : 'Reset Enrollment'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

